import React, { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import Loadable from '../layouts/full/shared/loadable/Loadable';

/* ***Layouts**** */
const FullLayout = Loadable(lazy(() => import('../layouts/full/FullLayout')));
const BlankLayout = Loadable(lazy(() => import('../layouts/blank/BlankLayout')));

/* ****Pages***** */

const Applications = Loadable(lazy(() => import('../views/employer-portal/application-list')));
const PayrollSummary = Loadable(lazy(() => import('../views/employer-portal/payroll-summary')));
const Invoices = Loadable(lazy(() => import('../views/employer-portal/invoice-list')));
const Settings = Loadable(lazy(() => import('../views/account-settings/Settings')));
const HelpPage = Loadable(lazy(() => import('../views/help/HelpPage')));
const DashboardCards = Loadable(lazy(() => import('../views/dashboard/Main')));
const InviteEmployees = Loadable(lazy(() => import('../views/invite-employees')));
const ApplyPage = Loadable(lazy(() => import('../views/apply-page/ApplyPage')));

// authentication
const EmployerSetPassword = Loadable(
  lazy(() => import('../views/authentication/auth1/EmployerSetPassword')),
);
const Login = Loadable(lazy(() => import('../views/authentication/auth1/Login')));
const ForgotPassword = Loadable(lazy(() => import('../views/authentication/auth1/ForgotPassword')));
const SetupNewPassword = Loadable(
  lazy(() => import('../views/authentication/auth1/SetupNewPassword')),
);
const ConfirmEmail = Loadable(lazy(() => import('../views/authentication/auth1/ConfirmEmail')));

const TwoSteps = Loadable(lazy(() => import('../views/authentication/auth1/TwoSteps')));
const Error = Loadable(lazy(() => import('../views/authentication/Error')));
const Maintenance = Loadable(lazy(() => import('../views/authentication/Maintenance')));
const VoucherDetails = Loadable(
  lazy(() => import('../views/application-details/ApplicationsDetails')),
);
const PdfFormPage = Loadable(lazy(() => import('../views/pdf-form/Form')));
const InviteYourEmployerPage = Loadable(
  lazy(() => import('../views/invite-employer/InviteYourEmployer')),
);
const EmployerPortalRegistrationPage = Loadable(
  lazy(() => import('../views/employer-portal-registration/EmployerPortalRegistrationPage')),
);
const EmployerPortalRegistrationHomePage = Loadable(
  lazy(() => import('../views/employer-portal-registration/EmployerPortalRegistrationHomePage')),
);

const EmployerLandingPage = Loadable(lazy(() => import('../views/employer-pages/EmployerLanding')));
const ViewWrapper = Loadable(lazy(() => import('./wrapper/ViewWrapper')));

import AuthGuard from 'src/guards/authGuard/AuthGuard';
import GuestGuard from 'src/guards/authGuard/GuestGaurd';

export const isNotSubdomain = () => {
  const host = window.location.host;
  const subDomain = host.split('.')[0];

  if (subDomain == 'dev') {
    return true;
  }

  return host.split('.').length <= 2;
};

export const basePortalURL = process.env.REACT_APP_PORTAL_ROUTE as string;
export const baseEmployeePortalURL = process.env.REACT_APP_EMPLOYEE_PORTAL_ROUTE;

export const employerTechURL = '/go/tech';
export const employerHomeURL = '/go/home';

const ExternalRedirect = ({ url }: { url: string }) => {
  React.useEffect(() => {
    window.location.href = url;
  }, [url]);

  return null;
};

export const b2bPages = [
  {
    path: '/',
    exact: true,
    element: <ExternalRedirect url={process.env.REACT_APP_LANDINGS_URL || ''} />,
  },
  {
    path: `${employerTechURL}/:accountNumber`,
    exact: true,
    element: (
      <ViewWrapper title="Employer Landing" description="Employer Landing page">
        <EmployerLandingPage />
      </ViewWrapper>
    ),
  },
  {
    path: `${employerHomeURL}/:accountNumber`,
    exact: true,
    element: (
      <ViewWrapper title="Employer Landing" description="Employer Landing page">
        <EmployerLandingPage />
      </ViewWrapper>
    ),
  },
  {
    path: `${employerTechURL}/:accountNumber/apply-now`,
    exact: true,
    element: (
      <ViewWrapper title="Apply now" description="Apply now page">
        <ApplyPage />
      </ViewWrapper>
    ),
  },
  {
    path: `${employerHomeURL}/:accountNumber/apply-now`,
    exact: true,
    element: (
      <ViewWrapper title="Apply now" description="Apply now page">
        <ApplyPage />
      </ViewWrapper>
    ),
  },

  { path: '*', element: <Navigate to={`/404`} /> },
  { path: `/404`, exact: true, element: <Error /> },
];

export const portalRoutes = [
  {
    path: `${basePortalURL}`,
    element: (
      <AuthGuard>
        <FullLayout />
      </AuthGuard>
    ),
    children: [
      { path: `${basePortalURL}`, element: <Navigate to={`${basePortalURL}/invite-employees`} /> },
      {
        path: `${basePortalURL}/dashboard`,
        exact: true,
        element: (
          <ViewWrapper title="Dashboard" description="Dashboard page">
            <DashboardCards />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/invite-employees`,
        exact: true,
        element: (
          <ViewWrapper title="Invite Employees" description="Invite Employees page">
            <InviteEmployees />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/applications`,
        exact: true,
        element: (
          <ViewWrapper title="Applications">
            <Applications />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/payroll-summary`,
        exact: true,
        element: (
          <ViewWrapper title="Payroll Summary">
            <PayrollSummary />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/invoices`,
        exact: true,
        element: (
          <ViewWrapper title="Invoices">
            <Invoices />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/account/settings`,
        exact: true,
        element: (
          <ViewWrapper title="Settings">
            <Settings />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/account/help`,
        exact: true,
        element: (
          <ViewWrapper title="Help Page">
            <HelpPage />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/application/view-details/:id`,
        exact: true,
        element: (
          <ViewWrapper title="Application Details">
            <VoucherDetails />
          </ViewWrapper>
        ),
      },
      { path: `${basePortalURL}/404`, exact: true, element: <Error /> },
      { path: '*', exact: true, element: <Navigate to={`/404`} /> },
    ],
  },
  {
    path: '/',
    element: (
      <GuestGuard>
        <BlankLayout />
      </GuestGuard>
    ),
    children: [
      { path: `${basePortalURL}/auth/404`, element: <Error /> },
      {
        path: `${basePortalURL}/auth/login`,
        element: (
          <ViewWrapper title="Login">
            <Login />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/auth/set-password`,
        element: (
          <ViewWrapper title="Login" description="Login page">
            <EmployerSetPassword />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/auth/forgot-password`,
        element: (
          <ViewWrapper title="Forgot Password" description="Forgot Password page">
            <ForgotPassword />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/auth/new-password-setup`,
        element: (
          <ViewWrapper title="New Password Setup" description="New Password Setup page">
            <SetupNewPassword />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/auth/confirm-email`,
        element: (
          <ViewWrapper title="Confirmation Email" description="Confirmation of an Email">
            <ConfirmEmail />
          </ViewWrapper>
        ),
      },
      {
        path: `${baseEmployeePortalURL}/auth/confirm-email`,
        element: (
          <ViewWrapper title="Confirmation Email" description="Confirmation of an Email">
            <ConfirmEmail />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/auth/two-steps`,
        element: (
          <ViewWrapper title="Two Steps" description="Two Steps page">
            <TwoSteps />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/auth/maintenance`,
        element: (
          <ViewWrapper title="Maintence">
            <Maintenance />
          </ViewWrapper>
        ),
      },
      {
        path: `/apply-now`,
        exact: true,
        element: (
          <ViewWrapper title="Sign Agreement" description="Sign Agreement page">
            <PdfFormPage />
          </ViewWrapper>
        ),
      },
      {
        path: `/invite-your-employer`,
        exact: true,
        element: (
          <ViewWrapper title="Invite Your Employer" description="Invite Your Employer page">
            <InviteYourEmployerPage />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/employer_portal/register`,
        exact: true,
        element: (
          <ViewWrapper
            title="Employer Sign-up | gogeta | Tech Scheme"
            description="This is a employer portal registration page"
          >
            <EmployerPortalRegistrationPage />
          </ViewWrapper>
        ),
      },
      {
        path: `${basePortalURL}/home/employer_portal/register`,
        exact: true,
        element: (
          <ViewWrapper
            title="Employer Sign-up | gogeta | Home Scheme"
            description="This is a employer portal registration page"
          >
            <EmployerPortalRegistrationHomePage />
          </ViewWrapper>
        ),
      },
      ...b2bPages,
    ],
  },
];
